import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronDown, ChevronUp, CheckCircle2, Circle, Truck, CreditCard, Banknote, Download, AlertCircle, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-orange-100 text-orange-800',
  paid: 'bg-green-100 text-green-800',
  demo_paid: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
  confirmed: 'bg-green-100 text-green-800',
  cod_pending: 'bg-orange-100 text-orange-800',
  payment_failed: 'bg-red-100 text-red-800',
  out_for_delivery: 'bg-blue-100 text-blue-800',
  refund_initiated: 'bg-violet-100 text-violet-800',
  refund_completed: 'bg-emerald-100 text-emerald-800',
};

const CANCEL_REASONS = [
  'Ordered by mistake',
  'Found a better price elsewhere',
  'Delivery time is too long',
  'Changed my mind',
  'Duplicate order placed',
  'Other',
];

// Statuses where user CAN cancel (before shipping)
const CANCELLABLE_STATUSES = new Set(['pending', 'cod_pending', 'processing', 'paid', 'demo_paid', 'confirmed']);
// Statuses after shipping (cannot cancel)
const POST_SHIPPING_STATUSES = new Set(['shipped', 'out_for_delivery', 'delivered']);
// Statuses where we should NOT show estimated delivery
const NO_DELIVERY_STATUSES = new Set(['cancelled', 'delivered', 'payment_failed', 'refund_initiated', 'refund_completed']);

// Returns a formatted estimated delivery date string (2 days after order date)
function getEstimatedDelivery(createdAt) {
  const d = new Date(createdAt);
  d.setDate(d.getDate() + 2);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Returns the actual delivery date from tracking_updates
function getActualDeliveryDate(trackingUpdates) {
  if (!trackingUpdates) return null;
  const deliveredEntry = [...trackingUpdates].reverse().find(t =>
    t.status?.toLowerCase().includes('delivered')
  );
  if (!deliveredEntry) return null;
  return new Date(deliveredEntry.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Cancel modal state
  const [cancelModal, setCancelModal] = useState({ open: false, orderId: null, invoiceNumber: '' });
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // "Why can't I cancel" info modal
  const [whyModal, setWhyModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders`, { withCredentials: true })
      .then(r => setOrders(r.data.orders))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async (orderId, invoiceNumber) => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/invoice`, { withCredentials: true });
      const invoiceHtml = `<html><head><meta charset="UTF-8"><title>Invoice ${data.invoice_number}</title>
        <style>*{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Segoe UI',sans-serif;background:#F8F5F2;color:#1C1209;padding:20px;}
        .invoice-wrapper{max-width:750px;margin:20px auto;background:#fff;border:1px solid #E5D9CC;border-radius:12px;padding:40px;box-shadow:0 10px 40px rgba(28,18,9,0.05);}
        h1{color:#C45E2A;font-size:26px;margin-bottom:6px}.meta{color:#8B6A52;font-size:13px;margin-bottom:24px;border-bottom:1px solid #F5E8D8;padding-bottom:12px;}
        .customer-box{padding:16px;border:1px solid #F5E8D8;border-radius:8px;background:#FDFBF9;margin-bottom:24px;}
        .section{margin-bottom:8px;font-size:14px;}.section:last-child{margin-bottom:0;}.label{font-weight:600;font-size:13px;color:#4A3728;display:inline-block;width:90px;}
        table{width:100%;border-collapse:collapse;margin:24px 0}th,td{border:1px solid #F5E8D8;padding:12px;text-align:left;font-size:13px}
        th{background:#F9F4EE;font-weight:700;color:#4A3728}.total{font-size:18px;font-weight:700;color:#C45E2A;text-align:right;margin-top:16px}
        .actions{margin:20px auto;text-align:center;max-width:750px;} .btn{background:#C45E2A;color:#fff;border:none;padding:12px 32px;border-radius:100px;font-size:15px;cursor:pointer;margin:0 8px;font-weight:600;transition:0.2s;}
        .btn:hover{background:#1C1209;transform:translateY(-2px);}.btn-outline{background:#fff;color:#C45E2A;border:2px solid #C45E2A}
        @media print{body{background:#fff;padding:0;}.invoice-wrapper{border:1px solid #000;box-shadow:none;margin:0;border-radius:0;padding:30px;max-width:100%;}.customer-box{border:1px solid #E5D9CC !important;-webkit-print-color-adjust:exact;}.actions{display:none}table th{background-color:#eee !important;-webkit-print-color-adjust:exact;}}
        </style></head><body>
        <div class="actions">
          <button class="btn" onclick="window.print()">Print Invoice</button>
          <button class="btn btn-outline" onclick="window.close()">Close</button>
        </div>
        <div class="invoice-wrapper">
          <h1>Pets Valley - Tax Invoice</h1>
          <div class="meta"><strong>Invoice:</strong> ${data.invoice_number} &nbsp;|&nbsp; <strong>Order No:</strong> ${orderId} &nbsp;|&nbsp; <strong>Date:</strong> ${new Date(data.created_at).toLocaleDateString()}</div>
          <div class="customer-box">
            <div class="section"><span class="label">Customer:</span> ${data.user_name} (${data.user_email})</div>
            <div class="section"><span class="label">Address:</span> ${(data.shipping_address?.line1||'')}, ${(data.shipping_address?.city||'')}, ${(data.shipping_address?.state||'')} - ${(data.shipping_address?.pincode||'')}</div>
            <div class="section"><span class="label">Phone:</span> ${data.phone||''}</div>
            <div class="section"><span class="label">Payment:</span> ${data.payment_method === 'cod' ? 'Cash on Delivery' : 'Online'}</div>
          </div>
          <table><tr><th>S.No.</th><th>Item Description</th><th>Qty</th><th>MRP</th><th>Discount</th><th>Price</th><th>Total</th></tr>
          ${(data.items||[]).map((item, idx) => {
            const desc = item.description ? `<br><span style="font-size:11px;color:#8B6A52">${item.description.substring(0,80)}${item.description.length>80?'...':''}</span>` : '';
            const mrp = item.mrp && item.mrp > item.price ? item.mrp : item.price;
            const discount = mrp - item.price;
            return `<tr><td style="text-align:center;">${idx + 1}</td><td><strong>${item.name}</strong>${desc}</td><td style="text-align:center;">${item.quantity}</td><td>Rs.${mrp.toLocaleString('en-IN')}</td><td>${discount > 0 ? `Rs.${discount.toLocaleString('en-IN')}` : '-'}</td><td>Rs.${item.price.toLocaleString('en-IN')}</td><td><strong>Rs.${(item.price * item.quantity).toLocaleString('en-IN')}</strong></td></tr>`;
          }).join('')}
          </table>
          <div style="display:flex;justify-content:flex-end;margin-top:16px;">
            <div style="text-align:right;min-width:200px;">
              <div style="font-size:14px;color:#4A3728;margin-bottom:12px;display:flex;justify-content:space-between;border-bottom:1px solid #E5D9CC;padding-bottom:12px;"><span>Subtotal:</span> <span>Rs.${(data.items||[]).reduce((s,i) => s+(i.price*i.quantity),0).toLocaleString('en-IN',{maximumFractionDigits:2,minimumFractionDigits:2})}</span></div>
              <div style="font-size:18px;font-weight:700;color:#C45E2A;display:flex;justify-content:space-between;"><span>Total Paid:</span> <span>Rs.${data.total.toLocaleString('en-IN',{maximumFractionDigits:2,minimumFractionDigits:2})}</span></div>
            </div>
          </div>
          <div style="margin-top:40px;display:flex;justify-content:flex-end;">
            <div style="text-align:center;width:180px;">
              <div style="font-family:'Brush Script MT','Comic Sans MS',cursive;font-size:32px;color:#00008B;transform:rotate(-5deg);margin-bottom:8px;">Pets Valley</div>
              <div style="border-top:1.5px solid #4A3728;padding-top:8px;font-size:12px;font-weight:700;color:#4A3728;text-transform:uppercase;letter-spacing:1px;">Authorized Signatory</div>
              <div style="font-size:10px;color:#8B6A52;margin-top:3px;">Digitally Verified</div>
            </div>
          </div>
        </div>
        </body></html>`;
      const blob = new Blob([invoiceHtml], { type: 'text/html' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('Failed to load invoice');
    }
  };

  const openCancelModal = (order) => {
    setCancelModal({ open: true, orderId: order.id, invoiceNumber: order.invoice_number });
    setSelectedReason('');
    setCustomReason('');
  };

  const closeCancelModal = () => {
    setCancelModal({ open: false, orderId: null, invoiceNumber: '' });
    setSelectedReason('');
    setCustomReason('');
  };

  const submitCancellation = async () => {
    const reason = selectedReason === 'Other' ? customReason.trim() : selectedReason;
    if (!reason) { toast.error('Please select or enter a reason for cancellation'); return; }
    setCancelling(true);
    try {
      await axios.post(`${API}/orders/${cancelModal.orderId}/cancel`, { reason }, { withCredentials: true });
      toast.success('Order cancelled successfully');
      setOrders(prev => prev.map(o =>
        o.id === cancelModal.orderId
          ? {
              ...o,
              status: 'cancelled',
              cancel_reason: reason,
              tracking_updates: [
                ...(o.tracking_updates || []),
                { status: 'Order Cancelled', note: reason, timestamp: new Date().toISOString() },
              ],
            }
          : o
      ));
      closeCancelModal();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="spinner"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream" data-testid="orders-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-heading text-3xl font-bold text-dark-brown mb-8">My Orders</h1>

        {orders.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-border-custom mx-auto mb-4" />
            <p className="font-body text-lg text-light-brown mb-4">No orders yet</p>
            <Link to="/products" className="font-body text-rust hover:underline">Start Shopping</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => {
              const isCancellable = CANCELLABLE_STATUSES.has(order.status);
              const isPostShipping = POST_SHIPPING_STATUSES.has(order.status);
              const isAlreadyCancelled = order.status === 'cancelled';

              return (
                <div key={order.id} className="bg-white rounded-xl border border-border-custom overflow-hidden" data-testid={`order-${order.id}`}>
                  <div className="p-5">
                    {/* Order header row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="font-body text-xs text-light-brown">Order #{order.id} &middot; {order.invoice_number}</div>
                        <div className="font-body text-xs text-light-brown">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          {order.payment_method === 'cod'
                            ? <Banknote className="w-3.5 h-3.5 text-green-600" />
                            : <CreditCard className="w-3.5 h-3.5 text-blue-600" />}
                          <span className="font-body text-xs text-mid-brown uppercase">{order.payment_method}</span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                          {order.status?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className="font-body font-bold text-rust" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
                          {'₹'}{order.total.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>

                    {/* Items chips */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {order.items.map((item, idx) => (
                        <Link key={idx} to={`/products/${item.product_id}`} className="flex items-center gap-2 bg-cream rounded-lg px-3 py-1.5 hover:bg-pale hover:shadow-sm transition-all cursor-pointer" style={{ textDecoration: 'none' }}>
                          {item.image_url && <img src={item.image_url} alt="" className="w-7 h-7 rounded object-cover" />}
                          <span className="font-body text-xs text-dark-brown hover:text-rust transition-colors">{item.name} <span className="text-light-brown">x{item.quantity}</span></span>
                        </Link>
                      ))}
                    </div>

                    {/* Delivered banner */}
                    {order.status === 'delivered' && (
                      <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 mb-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <p className="font-body text-xs text-emerald-800">
                          ✅ <strong>Delivered!</strong> Your order has been delivered on{' '}
                          <strong>{getActualDeliveryDate(order.tracking_updates) || getEstimatedDelivery(order.created_at)}</strong>
                        </p>
                      </div>
                    )}

                    {/* Estimated delivery date banner */}
                    {!NO_DELIVERY_STATUSES.has(order.status) && (
                      <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 mb-3">
                        <Truck className="w-4 h-4 text-emerald-600 shrink-0" />
                        <p className="font-body text-xs text-emerald-800">
                          🚚 <strong>Estimated Delivery:</strong> Your order will be delivered on{' '}
                          <strong>{getEstimatedDelivery(order.created_at)}</strong>
                        </p>
                      </div>
                    )}

                    {/* Payment failed banner */}
                    {order.status === 'payment_failed' && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-body text-xs font-semibold text-red-700">Order Not Placed — Payment Failed</p>
                          <p className="font-body text-xs text-red-600 mt-0.5">If money was deducted, it will be refunded within 5-7 business days.</p>
                        </div>
                      </div>
                    )}

                    {/* Cancelled banner */}
                    {isAlreadyCancelled && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-body text-xs font-semibold text-red-700">{order.cancelled_by === 'admin' ? 'Order Cancelled by Admin' : 'Order Cancelled'}</p>
                          {order.cancel_reason && <p className="font-body text-xs text-red-600 mt-0.5">Reason: {order.cancel_reason}</p>}
                          <p className="font-body text-xs text-red-500 mt-0.5">If you paid online, your refund will be processed within 5-7 business days.</p>
                        </div>
                      </div>
                    )}

                    {/* Post-shipping: cannot cancel banner */}
                    {isPostShipping && (
                      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                        <Truck className="w-4 h-4 text-amber-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-xs font-semibold text-amber-800">Cancellation Not Possible</p>
                          <p className="font-body text-xs text-amber-700 mt-0.5">Your order has already been shipped and cannot be cancelled.</p>
                        </div>
                        <button
                          onClick={() => setWhyModal(true)}
                          className="font-body text-xs font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900 transition-colors whitespace-nowrap shrink-0"
                          data-testid={`why-no-cancel-${order.id}`}
                        >
                          Why?
                        </button>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setExpanded(expanded === order.id ? null : order.id)} className="font-body text-xs rounded-full border-border-custom" data-testid={`track-order-${order.id}`}>
                        <Truck className="w-3 h-3 mr-1" /> Track Order {expanded === order.id ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                      </Button>

                      {order.status !== 'payment_failed' && (
                        <Button size="sm" variant="outline" onClick={() => downloadInvoice(order.id, order.invoice_number)} className="font-body text-xs rounded-full border-border-custom" data-testid={`view-invoice-${order.id}`}>
                          <Download className="w-3 h-3 mr-1" /> Invoice
                        </Button>
                      )}

                      {order.status === 'payment_failed' && (
                        <Link to="/contact">
                          <Button size="sm" variant="outline" className="font-body text-xs rounded-full border-red-300 text-red-600 hover:bg-red-50">
                            <MessageCircle className="w-3 h-3 mr-1" /> Contact Us
                          </Button>
                        </Link>
                      )}

                      {/* Cancel button — only for cancellable (pre-shipping) orders */}
                      {isCancellable && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCancelModal(order)}
                          className="font-body text-xs rounded-full border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all"
                          data-testid={`cancel-order-${order.id}`}
                        >
                          <AlertCircle className="w-3 h-3 mr-1" /> Cancel Order
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Tracking panel */}
                  {expanded === order.id && order.tracking_updates && (
                    <div className="border-t border-border-custom bg-cream/50 p-5">
                      <h4 className="font-body text-sm font-semibold text-dark-brown mb-4">Order Tracking</h4>
                      <div className="space-y-0">
                        {[...(order.tracking_updates || [])].reverse().map((t, i) => {
                          const isFailed = order.status === 'payment_failed';
                          const displayStatus = isFailed && t.status === 'Order Placed' ? 'Order Attempted' : t.status;
                          const displayNote = isFailed && t.note === 'Your order has been placed' ? 'Payment was initiated but not completed' : t.note;
                          return (
                            <div key={i} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <CheckCircle2 className={`w-5 h-5 shrink-0 ${i === 0 ? (isFailed ? 'text-red-500' : 'text-rust') : 'text-light-brown/80'}`} />
                                {i < order.tracking_updates.length - 1 && <div className="w-0.5 h-8 bg-border-custom"></div>}
                              </div>
                              <div className="pb-4">
                                <div className={`font-body text-sm font-medium ${i === 0 ? (isFailed ? 'text-red-600' : 'text-rust') : 'text-mid-brown'}`}>{displayStatus}</div>
                                {displayNote && <div className="font-body text-xs text-light-brown">{displayNote}</div>}
                                <div className="font-body text-xs text-light-brown">{new Date(t.timestamp).toLocaleString('en-IN')}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Cancel Order Modal ── */}
      {cancelModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(28,18,9,0.55)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold text-dark-brown">Cancel Order</h2>
                <p className="font-body text-xs text-light-brown">{cancelModal.invoiceNumber}</p>
              </div>
            </div>

            <p className="font-body text-sm text-mid-brown mb-4">Please tell us why you want to cancel this order.</p>

            <div className="space-y-2 mb-4">
              {CANCEL_REASONS.map(reason => (
                <label
                  key={reason}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedReason === reason ? 'border-red-400 bg-red-50' : 'border-border-custom hover:border-red-200 hover:bg-red-50/40'}`}
                >
                  <input
                    type="radio"
                    name="cancel_reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={() => { setSelectedReason(reason); setCustomReason(''); }}
                    className="accent-red-600"
                  />
                  <span className="font-body text-sm text-dark-brown">{reason}</span>
                </label>
              ))}
            </div>

            {selectedReason === 'Other' && (
              <textarea
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                placeholder="Please describe your reason..."
                rows={3}
                className="w-full border border-border-custom rounded-xl px-3 py-2.5 font-body text-sm text-dark-brown resize-none focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 mb-4"
              />
            )}

            <div className="flex gap-3 mt-2">
              <Button variant="outline" className="flex-1 font-body text-sm rounded-full border-border-custom" onClick={closeCancelModal} disabled={cancelling}>
                Keep Order
              </Button>
              <Button
                className="flex-1 font-body text-sm rounded-full bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
                onClick={submitCancellation}
                disabled={cancelling || !selectedReason || (selectedReason === 'Other' && !customReason.trim())}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Why Can't I Cancel? Modal ── */}
      {whyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(28,18,9,0.55)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="font-heading text-lg font-bold text-dark-brown">Why Can't I Cancel?</h2>
            </div>

            <div className="space-y-3 font-body text-sm text-mid-brown mb-5">
              <p>Once your order has been <strong className="text-dark-brown">shipped</strong>, it is handed over to our delivery partner and is already on its way to you.</p>
              <p>Cancellation is not possible because:</p>
              <ul className="list-disc list-inside space-y-1 pl-1 text-mid-brown">
                <li>The package is already in transit</li>
                <li>Our delivery partner has taken custody of it</li>
                <li>The shipment process cannot be reversed</li>
              </ul>
              <p>If you still do not want the order, you can <strong className="text-dark-brown">refuse delivery</strong> when it arrives. For refund queries, please contact our support team.</p>
            </div>

            <div className="flex gap-3">
              <Link to="/contact" className="flex-1">
                <Button variant="outline" className="w-full font-body text-sm rounded-full border-border-custom">
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Contact Support
                </Button>
              </Link>
              <Button className="flex-1 font-body text-sm rounded-full" onClick={() => setWhyModal(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
